import { withZod } from '@remix-validated-form/with-zod'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewWorkspaceIntegrationModal } from '../AddNewWorkspaceIntegrationModal'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import { Tooltip } from 'app/components/Tooltip'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { useLocationStore } from 'app/stores/StoreContext'

const createSchema = z.object({
  clientId: z.string().min(1, { message: `Client ID is required` }),
  clientSecret: z.string().min(1, { message: `Client Secret is required` }),
  jwt: z.string().min(1, { message: `JWT is required` }),
})

// On edit, secrets are never sent to the browser. Fields are pre-filled with a mask;
// leaving the mask in place keeps the stored value, so secrets are optional when editing.
const editSchema = z.object({
  clientId: z.string().min(1, { message: `Client ID is required` }),
  clientSecret: z.string().optional(),
  jwt: z.string().optional(),
})

// Must match SECRET_MASK in the api integrations route.
const SECRET_MASK = `••••••••`

export const createRingCentralWorkspaceIntegrationValidator = withZod(createSchema)
export const editRingCentralWorkspaceIntegrationValidator = withZod(editSchema)

export function RingCentralForm({
  onIntegrationChanged,
  currentValues,
  onSuccess,
  onBack,
}: {
  onIntegrationChanged: () => void
  currentValues?: {
    clientId?: string
    clientSecretSet?: boolean
    jwtSet?: boolean
  }
  /** Wizard continuation: called after a successful save instead of closing the modal. */
  onSuccess?: () => void
  /** Wizard back target: overrides returning to the workspace integration picker. */
  onBack?: () => void
}) {
  const [confirm] = useConfirmation()
  const [isRemovable, setIsRemovable] = useState(false)
  const locationStore = useLocationStore()

  useEffect(() => {
    if (currentValues) {
      bg(async () => {
        const result = await locationStore.fetchList()
        const data = result.data as any
        const hits = data?.locations?.hits ?? data?.hits ?? locationStore.hits
        if (hits.some((location) => location.ringCentralPhoneNumbers)) {
          setIsRemovable(false)
        } else {
          setIsRemovable(true)
        }
      })
    }
  }, [currentValues])

  const onCreateCompleted = (data: any) => {
    if (data.success) {
      notifySuccess(`RingCentral workspace integration created`)
      onIntegrationChanged()
      if (onSuccess) {
        onSuccess()
      } else {
        closeModal()
      }
    } else {
      notifyError(`Failed to create integration: ${data.error}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewWorkspaceIntegrationModal onIntegrationChanged={onIntegrationChanged} />,
      {
        title: `Add New Workspace Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove RingCentral Workspace Integration?`,
      description: `Removing this workspace integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    }).then(() => {
      return window
        .fetch(`/api/integrations/setup/ringcentral`, {
          method: `DELETE`,
          credentials: `include`,
          headers: { 'Content-Type': `application/json` },
          body: JSON.stringify({}),
        })
        .then(() => {
          notifySuccess(`RingCentral workspace integration removed`)
          onIntegrationChanged()
          closeModal()
        })
        .catch((error) => {
          notifyError(`Failed to remove integration: ${error}`)
        })
    })
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 p-4"
        validator={
          currentValues ? editRingCentralWorkspaceIntegrationValidator : createRingCentralWorkspaceIntegrationValidator
        }
        reloadDocument={false}
        shouldPreventDefault={true}
        onPreventDefault={(data) => onCreateCompleted(data)}
        action="/api/integrations/setup/ringcentral"
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        defaultValues={
          currentValues
            ? {
                clientId: currentValues.clientId,
                clientSecret: currentValues.clientSecretSet ? SECRET_MASK : undefined,
                jwt: currentValues.jwtSet ? SECRET_MASK : undefined,
              }
            : undefined
        }
      >
        <FormInput
          name="clientId"
          cols={6}
          required
          label="Client ID"
          labelClassName="text-body-sm text-strong"
          newLineError={false}
          showErrorIcon
        />
        <FormInput
          name="clientSecret"
          cols={6}
          required={!currentValues}
          label="Client Secret"
          labelClassName="text-body-sm text-strong"
          newLineError={false}
          showErrorIcon
        />
        <FormInput
          name="jwt"
          cols={6}
          required={!currentValues}
          label="JWT"
          labelClassName="text-body-sm text-strong"
          newLineError={false}
          showErrorIcon
        />
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-lg border-y border-default bg-surface-raised p-4">
        <div className="flex justify-between align-middle">
          <div>
            {currentValues ? (
              <Tooltip
                message={
                  !isRemovable
                    ? `You need to first delete all RingCentral location integrations to be able to remove this workspace integration.`
                    : undefined
                }
              >
                <Button label="Remove" mode="danger" size="lg" disabled={!isRemovable} onClick={() => onRemove()} />
              </Tooltip>
            ) : null}
          </div>
          <div className="flex justify-end gap-x-4 align-middle">
            {!currentValues ? (
              <Button
                label="Back"
                mode="white"
                size="lg"
                onClick={() => (onBack ? onBack() : goBackToSelectIntegrationModal())}
              />
            ) : (
              <Button label="Cancel" mode="white" size="lg" onClick={() => closeModal()} />
            )}

            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="lg"
              // onClick={() => setSaving(true)}
              // loading={saving}
            />
          </div>
        </div>
      </div>
    </>
  )
}
